import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { mountRootParcel, type Parcel } from 'single-spa';

import { loadMicrofrontend, type MicrofrontendName } from '../microfrontends';
import styles from './Microfrontend.module.css';

export interface IMicrofrontendProps {
    name: MicrofrontendName;
}

/**
 * single-spa parcel host: mounts the microfrontend into this div on mount and
 * unmounts it again when the parent window closes.
 */
export const Microfrontend: React.FC<IMicrofrontendProps> = ({ name }) => {
    const host = useRef<HTMLDivElement | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let parcel: Parcel | null = null;
        let cancelled = false;
        setError(null);

        loadMicrofrontend(name)
            .then((lifecycles) => {
                if (cancelled || !host.current) {
                    return;
                }
                parcel = mountRootParcel(lifecycles, {
                    domElement: host.current,
                });
            })
            .catch((reason: unknown) => {
                if (!cancelled) {
                    setError(`${name} could not be loaded: ${String(reason)}`);
                }
            });

        return () => {
            cancelled = true;
            const mounted = parcel;
            parcel = null;
            mounted?.unmount().catch(() => undefined);
        };
    }, [name]);

    return (
        <div className={styles.microfrontend} ref={host}>
            {error ? (
                <p className={styles.microfrontend__error}>{error}</p>
            ) : null}
        </div>
    );
};
